import React, { useRef, useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  IconButton,
  useTheme,
  Paper,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// 1. Importa tu registro unificado (ajusta la ruta según tu proyecto)
import { SYSTEMS_REGISTRY } from './systemsRegistry'; 

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ProPlanModal from '../../../components/pro/ProPlanModal';
import { useAuth } from '../../../../context/AuthContext.jsx';

const TRIALS_API = "https://apiweb.hitpoly.com/ajax/trialsController.php";

const TecnologiasYSuministros = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollContainerRef = useRef(null);
  const { user: authUser } = useAuth();

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [proActivo, setProActivo] = useState(false);
  const [proOrigen, setProOrigen] = useState(null);
  const [proFin, setProFin] = useState(null);
  const [isProInfoOpen, setIsProInfoOpen] = useState(false);
  const [selectedProItem, setSelectedProItem] = useState(null);
  const [trialVencido, setTrialVencido] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Verificar estado PRO
  useEffect(() => {
    const applyLocal = () => {
      try {
        const raw = localStorage.getItem("hitpoly_trial");
        if (raw) {
          const t = JSON.parse(raw);
          if (t && t.fin && new Date(t.fin).getTime() > Date.now()) {
            setProActivo(true);
            setProOrigen(t.origen || "prueba");
            setProFin(t.fin);
            return true;
          } else {
            localStorage.removeItem("hitpoly_trial");
          }
        }
      } catch (e) {}
      return false;
    };

    const uid = authUser?.id || authUser?.user_id;
    // Si localStorage tiene prueba vigente, usar inmediatamente sin consultar API
    try {
      const raw = localStorage.getItem("hitpoly_trial");
          if (raw) {
            const t = JSON.parse(raw);
            if (t && t.fin && new Date(t.fin).getTime() > Date.now()) {
              setProActivo(true);
              setProOrigen("prueba");
              setProFin(t.fin);
              return;
            }
          }
    } catch (e) {}
    if (!uid) { applyLocal(); return; }

    fetch(TRIALS_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ accion: "estado", usuario_id: uid }),
    })
      .then((r) => r.json())
      .then((d) => {
        const rawLocal = localStorage.getItem("hitpoly_trial");
        const finFuture = (d && d.fin && new Date(d.fin).getTime() > Date.now());
        const activoReal = (d && d.status === "success" && (d.activo || finFuture));
        if (activoReal) {
          setProActivo(true);
          setProOrigen(d.origen || null);
          setProFin(d.fin || null);
          setTrialVencido(false);
        } else if (d && d.status === "success" && !d.activo) {
          try {
            if (rawLocal) {
              const t = JSON.parse(rawLocal);
              if (t && t.fin && new Date(t.fin).getTime() <= Date.now()) {
                localStorage.removeItem("hitpoly_trial");
                setProActivo(false);
                setProOrigen(null);
                setProFin(null);
                setTrialVencido(true);
              } else {
                localStorage.removeItem("hitpoly_trial");
                setProActivo(false);
                setProOrigen(null);
                setProFin(null);
                setTrialVencido(false);
              }
            } else {
              localStorage.removeItem("hitpoly_trial");
              setProActivo(false);
              setProOrigen(null);
              setProFin(null);
              setTrialVencido(false);
            }
          } catch (e) {
            localStorage.removeItem("hitpoly_trial");
            setProActivo(false);
            setProOrigen(null);
            setProFin(null);
            setTrialVencido(false);
          }
        } else {
          const result = applyLocal();
          if (!result) {
            try {
              if (rawLocal) {
                const t = JSON.parse(rawLocal);
                if (t && t.fin && new Date(t.fin).getTime() <= Date.now()) {
                  localStorage.removeItem("hitpoly_trial");
                  setTrialVencido(true);
                } else {
                  localStorage.removeItem("hitpoly_trial");
                  setTrialVencido(false);
                }
              } else {
                setTrialVencido(false);
              }
            } catch (e) {
              setTrialVencido(false);
            }
          }
        }
      })
      .catch((err) => {
        applyLocal();
        try {
          const raw = localStorage.getItem("hitpoly_trial");
          if (raw) {
            const t = JSON.parse(raw);
            if (t && t.fin && new Date(t.fin).getTime() <= Date.now()) {
              localStorage.removeItem("hitpoly_trial");
              setTrialVencido(true);
            } else {
              localStorage.removeItem("hitpoly_trial");
              setTrialVencido(false);
            }
          } else {
            setTrialVencido(false);
          }
        } catch (e) {
          setTrialVencido(false);
        }
      });
  }, [authUser]);

  // Escuchar evento de trial activado desde otros componentes
  useEffect(() => {
    const handleProTrialStarted = () => {
      // Re-verificar estado desde localStorage
      try {
        const raw = localStorage.getItem("hitpoly_trial");
        if (raw) {
          const t = JSON.parse(raw);
          if (t && t.fin && new Date(t.fin).getTime() > Date.now()) {
            setProActivo(true);
            setProOrigen(t.origen || "prueba");
            setProFin(t.fin);
          }
        }
      } catch (e) {}
    };

    window.addEventListener("pro-trial-started", handleProTrialStarted);
    return () => window.removeEventListener("pro-trial-started", handleProTrialStarted);
  }, []);

  const openProInfoModal = (item) => {
    setSelectedProItem(item);
    setIsProInfoOpen(true);
  };

  // MANEJO DE NAVEGACIÓN UNIFICADO
  // Si el servicio es Pro y está activo, va a los servicios de Bolsa con IDs 11 (profesional) o 12 (empresa)
  // Si no está activo, abre el popup Pro.
  // Para sistemas internos (comunidad, mensajes) o no-Pro, va a /systems/{id}
  const handleNavigation = (service) => {
    if (service.isPro) {
      if (!proActivo) {
        openProInfoModal(service);
        return;
      }
      // Pro activo: usar IDs reales de los servicios en Bolsa
      const profileType = service.profileType || (service.id === 'bolsa' ? 'profesional' : 'empresa');
      const serviceId = profileType === 'profesional' || profileType === 'professional' ? 11 : 12;
      const routeType = profileType === 'profesional' || profileType === 'professional' ? 'servicio-profesional' : 'servicio-empresa';
      const url = `/systems/bolsa/${routeType}/${serviceId}`;
      localStorage.removeItem(`last_path_${service.id}`);
      navigate(url + `?reset=true`);
      return;
    }
    // Limpiar caché de rutas guardadas para forzar carga fresca
    localStorage.removeItem(`last_path_${service.id}`);
    navigate(`/systems/${service.id}?reset=true`);
  };

  // --- LÓGICA DE SCROLL ---
  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowLeftArrow(container.scrollLeft > 10);
      setShowRightArrow(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -350 : 350,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScroll();
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      }
    };
  }, []);

  // SUB-COMPONENTE TARJETA
  const ServiceCard = ({ service }) => {
    const { icon: Icon, title, label, subtitle, color, bgColor, isPro } = service;
    
    // Determinar qué badge mostrar según el estado Pro
    const renderBadge = () => {
      if (!isPro) return null;
      
      // Si el usuario tiene Pro activo, mostrar badge verde de "ACTIVO"
      if (proActivo) {
        return (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: '#16a34a',
              color: '#fff',
              fontSize: '9px',
              fontWeight: 800,
              letterSpacing: '0.5px',
              px: 1,
              py: 0.2,
              borderRadius: '10px',
            }}
          >
            ACTIVO
          </Box>
        );
      }
      
      // Si no tiene Pro activo, mostrar badge rosa de "PRO"
      return (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: '#F21C63',
            color: '#fff',
            fontSize: '9px',
            fontWeight: 800,
            letterSpacing: '0.5px',
            px: 1,
            py: 0.2,
            borderRadius: '10px',
          }}
        >
          PRO
        </Box>
      );
    };
    
    return (
      <Paper
        elevation={2}
        onClick={() => handleNavigation(service)}
        sx={{
          minWidth: isMobile ? 220 : 230,
          height: 150,
          flexShrink: 0,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          p: 2,
          background: bgColor,
          cursor: 'pointer',
          transition: '0.25s ease',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 7px 16px rgba(0,0,0,0.10)'
          }
        }}
      >
        {renderBadge()}

        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1A1A1A' }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.8, mt: -0.5 }}>
            {subtitle}
          </Typography>
        </Box>

        <Box
          sx={{
            px: 1.3,
            py: 0.3,
            borderRadius: 1.5,
            fontSize: 11,
            fontWeight: 700,
            color: 'white',
            background: color,
            width: 'fit-content'
          }}
        >
          {label}
        </Box>

        <Box sx={{
          position: 'absolute',
          right: -10,
          bottom: -10,
          opacity: 0.20,
          filter: 'blur(0.5px)',
          transform: 'rotate(12deg)',
        }}>
          <Icon sx={{ fontSize: 65, color }} />
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ width: '100%', mt: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#211E26', pl: 1 }}>
        Tecnologías y Servicios
      </Typography>

      <Box sx={{ position: 'relative' }}>
        {!isMobile && showLeftArrow && (
          <IconButton
            onClick={() => scroll('left')}
            sx={{
              position: 'absolute',
              left: -15,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              backgroundColor: 'white',
              boxShadow: 3,
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}

        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            py: 1,
            px: 1,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {SYSTEMS_REGISTRY.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </Box>

        {!isMobile && showRightArrow && (
          <IconButton
            onClick={() => scroll('right')}
            sx={{
              position: 'absolute',
              right: -15,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              backgroundColor: 'white',
              boxShadow: 3,
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <ProPlanModal
        open={isProInfoOpen}
        vencido={trialVencido}
        onClose={() => { setIsProInfoOpen(false); setSelectedProItem(null); setShowSuccess(false); }}
        herramienta={selectedProItem?.title}
        onTrialStarted={(fin) => {
          setProActivo(true);
          setProOrigen("prueba");
          setProFin(fin || null);
          setIsProInfoOpen(false);
          setShowSuccess(true);
        }}
      />
    </Box>
  );
};

export default TecnologiasYSuministros;